import { Alert } from 'src/design/atoms/Alert';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useFormik } from 'formik';
import React from 'react';
import { useUpdateJobTemplates } from 'src/api/queries/jobs/useJobMutations';
import { ChangeTemplateSelector } from 'src/app/selectors/ChangeTemplateSelector';
import { DocumentTemplateSelector } from 'src/app/selectors/DocumentTemplateSelector';
import { MilestoneTemplateSelector } from 'src/app/selectors/MilestoneTemplateSelector';
import { useJobs } from 'src/hooks/useJobs';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { Header } from 'src/design/organisms/Header';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  documentTemplateId: Yup.string().nullable(),
  milestoneTemplateId: Yup.string().nullable(),
  changeTemplateId: Yup.string().nullable(),
});

export const Templates: React.FC = () => {
  const { selectedJob } = useJobs();
  const updateTemplates = useUpdateJobTemplates();

  const formik = useFormik({
    initialValues: {
      documentTemplateId: selectedJob?.documentTemplateId || null,
      milestoneTemplateId: selectedJob?.milestoneTemplateId || null,
      changeTemplateId: selectedJob?.changeTemplateId || null,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async () => {
      // This will be handled by the confirmation button
    },
  });

  const saveTemplateSettings = async () => {
    if (!selectedJob) return;
    await updateTemplates.mutateAsync({
      jobId: selectedJob.id,
      data: {
        documentTemplateId: formik.values.documentTemplateId,
        milestoneTemplateId: formik.values.milestoneTemplateId,
        changeTemplateId: formik.values.changeTemplateId,
      },
    });
    formik.resetForm({ values: formik.values });
  };

  return (
    <>
      <Header
        title="Templates"
        helpText="Manage the templates for the job"
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'Templates', href: '' },
          ],
        }}
      />

      <Paper p="md" mt="md">
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle />} title="Warning" color="yellow">
            <Text c="black" size="sm">
              Changing the template will not remove any existing data tied to
              the old template for the active job. You will need to manually
              review the data and update if needed.
            </Text>
          </Alert>

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <DocumentTemplateSelector
                value={formik.values.documentTemplateId}
                onChange={(value) =>
                  formik.setFieldValue('documentTemplateId', value)
                }
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MilestoneTemplateSelector
                value={formik.values.milestoneTemplateId}
                onChange={(value) =>
                  formik.setFieldValue('milestoneTemplateId', value)
                }
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ChangeTemplateSelector
                value={formik.values.changeTemplateId}
                onChange={(value) =>
                  formik.setFieldValue('changeTemplateId', value)
                }
              />
            </Grid.Col>
          </Grid>

          <Group justify="flex-end">
            <InlineConfirmButton
              disabled={!formik.dirty || !formik.isValid}
              loading={updateTemplates.isPending}
              confirmButtonProps={{
                children: 'Confirm Save',
                color: 'red',
              }}
              onConfirmClick={saveTemplateSettings}
            >
              Save Settings
            </InlineConfirmButton>
          </Group>
        </Stack>
      </Paper>
    </>
  );
};
